import React, { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'umi';
import TreeItem from './TreeItem';
import './index.less';
import { formatPercent } from '../../pages/monetization/SalesBreakdown/component/SalesBreakdownTreeChart/_utils';

interface TreeInterface {
  data: any;
  styleConfig: any;
  isSingleExpands?: boolean; // 是否只打开一个节点
  getSelectedNode: Function; // 父级获取展开后的节点信息
}

const TreeNode = (props: any) => {
  const { node, toggleNode, styleConfig, className } = props;
  const { width, height, backgroundColor } = styleConfig || { width: 12, height: 8, backgroundColor: '#ecebea' };
  const { formatMessage } = useIntl();
  const [showLength, setShowLength] = useState(10);

  const setPlaceHolderPosition = () => {
    const depthFirstDomArr = document?.getElementsByClassName("dbd-tree-node-depth-2") || [];
    const newDepthFirstDomHeight = depthFirstDomArr?.[0]?.offsetHeight || 0;
    const newDepthLastDomHeight = depthFirstDomArr?.[depthFirstDomArr?.length - 1]?.offsetHeight || 0;
    const placeHolderDom = document?.getElementsByClassName("dbd-tree-node-depth-1-placeholder")?.[0];
    placeHolderDom?.style && (placeHolderDom.style.top = `${newDepthFirstDomHeight / 2}px`);
    placeHolderDom?.style && (placeHolderDom.style.bottom = `${newDepthLastDomHeight / 2}px`);
  };

  const nodeList = useMemo(() => 
    // 过滤数据为0的数据
    node?.children?.filter((child: any) => !(child.depth !== 1 && child.value === 0)) || []
  , [node]);

  useEffect(() => {
    setPlaceHolderPosition();
  }, [showLength]);

  const toggleNodeFunc = () => {
    if (!((node.value === 0 || node.value === '0' || node?.value === '' || node?.value === '-' || node?.value === '0.00%') && node.depth === 1)) {  // 将下一行的条件修改为未满足展开的节点情况
      toggleNode(node);
      // 不延时的话，拿到的是旧的值
      setTimeout(() => {
        setPlaceHolderPosition();
      }, 0);
    }
  };

  const clickMore = (list: Array<Object>) => {
    // showLength兜底
    if (showLength >= list?.length) return;
    // 最大长度为数据数组长度
    if (showLength + 10 >= list?.length) return setShowLength(list?.length);
    // 每次点击more展开10条
    setShowLength(showLength + 10);
  };

  const clickClose = (_nodeList: any) => {
    // 最小长度为10条
    if (showLength - 10 < 10) return setShowLength(10);
    // 每次点击close收起10条
    setShowLength(showLength - 10);
  };

  const nodePercent = {
    value: node?.value / node?.sumValue,
    formatRule: "percent2Decimal",
  };

  return (
    <div className={`dbd-tree-node ${className ? className : `dbd-tree-node-depth-${node?.depth}`}`}>
      <div className={`dbd-tree-node-content  dbd-tree-node-content-${node?.collapsed ? 'collapsed' : 'expended'}`} onClick={toggleNodeFunc}>
        <TreeItem
          dataConfig={{
            topDataConfig: {
              name: node?.name,
              icon: node?.icon,
              value: node?.value,
              valueShow: node?.valueShow,
              hidePercent: node?.hidePercent,
              percent: node?.sumValue && formatPercent(nodePercent),
              depth: node?.depth,
              styleConfig: {
                width
              },
              longestNameWidth: node?.longestNameWidth,
              valueWidth: node?.valueWidth
            },
            bottomDataConfig: {
              progressBoxConfig: {
                width,
                height,
                backgroundColor: backgroundColor || '#ecebea'
              },
              depth: node?.depth,
              progressContentConfig: {
                percent: node?.sumValue ? (node?.value / node?.sumValue) : 0,
                node,
                height,
                colorFilledProcess: node?.colorFilledProcess || false,
                backgroundColor: node?.backgroundColor || '#107c10'
              },
            }
          }}
        />
      </div>
      {node?.children && !node?.collapsed && (
        <div className="dbd-tree-node-children">
          <div className={`dbd-tree-node-content-placeholder dbd-tree-node-depth-${node?.depth}-placeholder`}></div>
          {nodeList?.length > 10 ? <div>
            {nodeList?.map((child: { name: string; }, index: number) => (
              <TreeNode className={index >= showLength ? 'dbd-tree-node-hidden' : ''} key={child?.name} node={child} toggleNode={toggleNode} styleConfig={styleConfig} />
            ))}
            <div className={`dbd-tree-node dbd-tree-node-depth-${node?.depth + 1}`}>
              <div className='dbd-tree-node-content dbd-tree-node-content-click'>
                {showLength < nodeList?.length && <div onClick={() => clickMore(nodeList)}>{formatMessage({ id: 'common_more' })}</div>}
                {showLength > 10 && <div onClick={() => clickClose(nodeList)}>{formatMessage({ id: 'common_close' })}</div>}
              </div>
            </div>
          </div> :
            <div>
              {nodeList?.map((child: { name: string; }) => (
                <TreeNode key={child?.name} node={child} toggleNode={toggleNode} styleConfig={styleConfig} />
              ))}
            </div>}
        </div>
      )}
    </div>
  );
};

const calculateLongestNameWidth = (data: any, fontStyle = "14px Arial") => {
  let longestNameChildren = ""; // children 层级最长名字
  let longestNameChildrenChildren = ""; // children.children 层级最长名字
  let longestSecLevelValue = ''; // children 层级最大值
  let longestThiLevelValue = ''; // children.children 层级最大值

  const findLongestName = (node: any, depth = 0) => {
    if (node.name) {
      // 中英文混合判定会异常
      if (depth === 1) {
        const { name = '', valueShow = '' } = node || {}; // Update max value
        if (name.length > longestNameChildren.length) {
          longestNameChildren = name; // Update max name
        }
        if (valueShow.length > longestSecLevelValue.length) {
          longestSecLevelValue = valueShow; // Update max value
        }
      }
      if (depth === 2) {
        const { name = '', valueShow = '' } = node || {}; // Update max value
        if (name.length > longestNameChildrenChildren.length) {
          longestNameChildrenChildren = name; // Update max name
        }
        if (valueShow.length > longestThiLevelValue.length) {
          longestThiLevelValue = valueShow; // Update max value
        }
      }
    }
    if (node.children) {
      node.children.forEach((child: any) => findLongestName(child, depth + 1));
    }
  };

  findLongestName(data);

  // Create a temporary canvas to measure text width
  // const canvas = document.createElement("canvas");
  // const context = canvas.getContext("2d");
  
  // 需要加上百分比的宽度
  const secValueWidth = longestSecLevelValue.length * 10 + 55;
  const thiValueWidth = longestThiLevelValue.length * 10 + 55;

  const secNameWidth = longestNameChildren.length * 7;
  const thiNameWidth = longestNameChildrenChildren.length * 7;

  // if (context) {
  //   context.font = fontStyle; // Match the font style of your component
  //   return {
  //     secValueWidth,
  //     thiValueWidth,
  //     childrenWidth: context.measureText(longestNameChildren).width,
  //     childrenChildrenWidth: context.measureText(longestNameChildrenChildren).width,
  //   };
  // }

  // Fallback widths in case of failure
  return {
    overallWidth: 0,
    childrenWidth: secNameWidth,
    childrenChildrenWidth: thiNameWidth,
    secValueWidth,
    thiValueWidth,
  };
};


const TreeComponent = (props: TreeInterface) => {
  const { data, styleConfig, isSingleExpands = true, getSelectedNode } = props || {};
  const [treeData, setTreeData] = useState();

  useEffect(() => {
    const setInitialCollapseStatus = (treeData: any) => {
      const dfs = (node: any) => {
        if ((node.value === 0 || node.value === '0' || node?.value === '' || node?.value === '-' || node?.value === '0.00%') && node.depth === 1) {
          node.collapsed = true;
        }
        if (node.children) {
          node.children.forEach((child: any) => dfs(child));
        }
      };
      dfs(treeData);
      return treeData;
    };

    const addPlaceholderWidth = (node: any, childrenWidth: number, childrenChildrenWidth: number, secValueWidth: number, thiValueWidth: number) => {
      if (node.children) {
        node.children = node.children.map((child: any) => {
          // 为当前子节点添加宽度
          const updatedChild = {
            ...child,
            longestNameWidth: childrenWidth,
            valueWidth: secValueWidth
          };
    
          // 为子节点的 children 添加宽度（更深层次）
          if (child.children) {
            updatedChild.children = child.children.map((subChild: any) => ({
              ...subChild,
              longestNameWidth: childrenChildrenWidth,
              valueWidth: thiValueWidth
            }));
          }
    
          return updatedChild;
        });
      }
      return {
        ...node,
      };
    };
    

    if (data) {
      const initialTreeData = setInitialCollapseStatus(data);
      const {childrenWidth, childrenChildrenWidth, secValueWidth, thiValueWidth} = calculateLongestNameWidth(data);

      // Update treeData with placeholderWidthx
      const updatedTreeData = addPlaceholderWidth(initialTreeData, childrenWidth, childrenChildrenWidth, secValueWidth, thiValueWidth);
      setTreeData(updatedTreeData);
    }
  }, [data]);


  const toggleNode = (node: any) => {
    const updateNode = (treeNode: any) => {
      if (treeNode?.name === node?.name) {
        treeNode.collapsed = !treeNode?.collapsed;
      } else if (treeNode?.children) {
        // treeNode.collapsed = true
        treeNode?.children.map(updateNode);
      }
      return treeNode;
    };

    const updatedTreeData = updateNode(Object.assign({}, treeData));
    setTreeData(updatedTreeData);
  };

  const toggleSingleNode = (node: any) => {
    const updateNode = (treeNode: any, parentNode: any = null) => {
      if (treeNode?.name === node?.name) {
        treeNode.collapsed = !treeNode?.collapsed;
        // 如果正在展开节点，则关闭同级节点
        if (!treeNode.collapsed && parentNode) {
          parentNode.children.forEach((siblingNode: any) => {
            if (siblingNode.name !== treeNode.name) {
              siblingNode.collapsed = true;
            }
          });
        }
      } else if (treeNode?.children) {
        treeNode?.children.map((childNode: any) => updateNode(childNode, treeNode));
      }
      return treeNode;
    };
    const updatedTreeData = updateNode(Object.assign({}, treeData));
    getSelectedNode(updatedTreeData);
    setTreeData(updatedTreeData);
  };

  return (
    <div className="dbd-tree-chart">
      <TreeNode
        key={treeData?.name + treeData?.value}
        node={treeData}
        toggleNode={isSingleExpands ? toggleSingleNode : toggleNode}
        styleConfig={styleConfig}
      />
    </div>
  );
};

export default TreeComponent;